import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getToday,
  getDay,
  getHistory,
  getHoursSummary,
  getRequests,
  getWeekApprovals,
  clockIn,
  startBreak,
  endBreak,
  clockOut,
  submitDay,
  updateSegment,
  patchDay,
  createRequest,
  writeDay,
  getTimesheetPolicy,
  type TimesheetPolicyResponse,
} from '@/api/timesheet';
import {
  SegmentType,
  type CreateRequestPayload,
  type GetHistoryParams,
  type GetRequestsParams,
  type GetWeekApprovalsParams,
  type PatchDayPayload,
  type TimesheetHistoryResponse,
  type TimesheetHoursSummaryResponse,
  type TimesheetTodayResponse,
  type UpdateSegmentPayload,
} from '@/types/timesheet';
import { QueryKeys } from '@gokrew/models/constants';

type QC = ReturnType<typeof useQueryClient>;

const invalidateTimesheetData = (qc: QC) => {
  qc.invalidateQueries({ queryKey: [QueryKeys.TimesheetToday] });
  qc.invalidateQueries({ queryKey: [QueryKeys.TimesheetHistory] });
  qc.invalidateQueries({ queryKey: [QueryKeys.TimesheetHoursSummary] });
  qc.invalidateQueries({ queryKey: [QueryKeys.TimesheetWeekApprovals] });
};

export const useGetToday = () => {
  return useQuery<TimesheetTodayResponse>({
    queryKey: [QueryKeys.TimesheetToday],
    queryFn: () => getToday(),
  });
};

export const useGetDay = (date: string) => {
  return useQuery({
    queryKey: [QueryKeys.TimesheetDay, date],
    queryFn: () => getDay(date),
    enabled: !!date,
    placeholderData: keepPreviousData,
  });
};

export const useGetHistory = (params: GetHistoryParams) => {
  return useQuery<TimesheetHistoryResponse>({
    queryKey: [QueryKeys.TimesheetHistory, params],
    queryFn: () => getHistory(params),
  });
};

export const useGetHoursSummary = (params: GetHistoryParams) => {
  return useQuery<TimesheetHoursSummaryResponse>({
    queryKey: [QueryKeys.TimesheetHoursSummary, params],
    queryFn: () => getHoursSummary(params),
  });
};

export const useGetRequests = (params: GetRequestsParams) => {
  return useQuery({
    queryKey: [QueryKeys.TimesheetRequests, params],
    queryFn: () => getRequests(params),
  });
};

export const useGetWeekApprovals = (params: GetWeekApprovalsParams) => {
  return useQuery({
    queryKey: [QueryKeys.TimesheetWeekApprovals, params],
    queryFn: () => getWeekApprovals(params),
  });
};

export const useClockIn = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => clockIn(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: [QueryKeys.TimesheetToday] });
      const previous = queryClient.getQueryData([QueryKeys.TimesheetToday]);
      queryClient.setQueryData([QueryKeys.TimesheetToday], (old: TimesheetTodayResponse | undefined) => ({
        ...old,
        data: {
          ...old?.data,
          openSegment: { type: SegmentType.Clock, startTime: new Date().toTimeString().slice(0, 8), endTime: null },
        },
      }));
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      queryClient.setQueryData([QueryKeys.TimesheetToday], ctx?.previous);
    },
    onSettled: () => invalidateTimesheetData(queryClient),
  });
};

export const useStartBreak = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (employeeNote?: string) => startBreak(employeeNote),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: [QueryKeys.TimesheetToday] });
      const previous = queryClient.getQueryData([QueryKeys.TimesheetToday]);
      queryClient.setQueryData([QueryKeys.TimesheetToday], (old: TimesheetTodayResponse | undefined) => ({
        ...old,
        data: { ...old?.data, openSegment: { type: SegmentType.Break, startTime: new Date().toTimeString().slice(0, 8), endTime: null } },
      }));
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      queryClient.setQueryData([QueryKeys.TimesheetToday], ctx?.previous);
    },
    onSettled: () => invalidateTimesheetData(queryClient),
  });
};

export const useEndBreak = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (employeeNote?: string) => endBreak(employeeNote),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: [QueryKeys.TimesheetToday] });
      const previous = queryClient.getQueryData([QueryKeys.TimesheetToday]);
      queryClient.setQueryData([QueryKeys.TimesheetToday], (old: TimesheetTodayResponse | undefined) => ({
        ...old,
        data: {
          ...old?.data,
          openSegment: { type: SegmentType.Clock, startTime: new Date().toTimeString().slice(0, 8), endTime: null },
        },
      }));
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      queryClient.setQueryData([QueryKeys.TimesheetToday], ctx?.previous);
    },
    onSettled: () => invalidateTimesheetData(queryClient),
  });
};

export const useClockOut = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (employeeNote?: string) => clockOut(employeeNote),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: [QueryKeys.TimesheetToday] });
      const previous = queryClient.getQueryData([QueryKeys.TimesheetToday]);
      queryClient.setQueryData([QueryKeys.TimesheetToday], (old: TimesheetTodayResponse | undefined) => {
        if (!old) return old;
        return { ...old, data: { ...old?.data, openSegment: null } };
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      queryClient.setQueryData([QueryKeys.TimesheetToday], ctx?.previous);
    },
    onSettled: () => invalidateTimesheetData(queryClient),
  });
};

export const useSubmitDay = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (date: string) => submitDay(date),
    onSuccess: () => invalidateTimesheetData(queryClient),
  });
};

export const useUpdateSegment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSegmentPayload }) =>
      updateSegment(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QueryKeys.TimesheetToday] }),
  });
};

export const usePatchDay = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ date, payload }: { date: string; payload: PatchDayPayload }) =>
      patchDay(date, payload),
    onSuccess: () => invalidateTimesheetData(queryClient),
  });
};

export const useCreateRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRequestPayload) => createRequest(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QueryKeys.TimesheetRequests] }),
  });
};

export const useWriteDay = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof writeDay>[0]) => writeDay(payload),
    onSuccess: () => invalidateTimesheetData(queryClient),
  });
};

export const useTimesheetPolicy = (options?: { enabled?: boolean }) => {
  return useQuery<TimesheetPolicyResponse>({
    queryKey: [QueryKeys.TimesheetSettings],
    queryFn: () => getTimesheetPolicy(),
    staleTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    ...options,
  });
};
